import { push } from "connected-react-router";
import PropTypes from "prop-types";
import React, { Component } from "react";
import DocumentTitle from "react-document-title";
import { connect } from "react-redux";

import { notifications } from "../../base/actions";
import { userSelectors } from "../../nav/selectors";
import {
  groups,
  policies,
  resources,
  resourceTypes,
  roles,
  services,
  users
} from "../../policies/actions";
import { UNDO_REMOVE_POLICY_TIMER } from "../../policies/constants";
import { PolicyShape } from "../../policies/proptypes";
import policiesSelectors from "../../policies/selectors/policies";
import {
  MIN_WIDTH_ACTIONS,
  MIN_WIDTH_ENTITIES,
  MIN_WIDTH_RESOURCES,
  MIN_WIDTH_ROLES,
  MIN_WIDTH_SERVICES
} from "../../shared/constants";
import ListViewActionBar from "../components/ListViewActionBar";
import ListViewTable from "../components/ListViewTable";
import { ListTablePolicyShape } from "../proptypes";
import listTablePoliciesSelector from "../selectors";

class ListView extends Component {
  title = "RBAC - List View";

  state = {
    columns: [
      { label: "Groups & Users", key: "entity", minWidth: MIN_WIDTH_ENTITIES },
      { label: "Services", key: "service", minWidth: MIN_WIDTH_SERVICES },
      { label: "Scopes", key: "resource", minWidth: MIN_WIDTH_RESOURCES },
      { label: "Roles", key: "role", minWidth: MIN_WIDTH_ROLES },
      { label: "", key: "actions", minWidth: MIN_WIDTH_ACTIONS }
    ],
    orderBy: ["entity", "service", "resource", "role", "actions"],
    selectedPolicies: []
  };

  getPoliciesByOrder = () => {
    const { orderBy } = this.state;
    const { policiesByGroup, policiesByRole, policiesByService } = this.props;

    switch (orderBy[0]) {
      case "service":
        return policiesByService;
      case "role":
        return policiesByRole;
      default:
        return policiesByGroup;
    }
  };

  openRoles = uri => {
    const { setCurrentRole, push } = this.props;
    setCurrentRole(uri);
    push("/listview/roles");
  };

  openResources = uri => {
    const { setCurrentResourceType, push } = this.props;
    setCurrentResourceType(uri);
    push("/listview/scopes");
  };

  updateOrder = selectedOrder => {
    const { orderBy } = this.state;

    if (selectedOrder === orderBy[0]) {
      return;
    }

    let newOrder;
    switch (selectedOrder) {
      case "service":
        newOrder = ["service", "resource", "role", "entity", "actions"];
        break;
      case "role":
        newOrder = ["role", "service", "resource", "entity", "actions"];
        break;
      default:
        newOrder = ["entity", "service", "resource", "role", "actions"];
    }

    this.setState({
      orderBy: newOrder,
      selectedPolicies: []
    });
  };

  selectPolicies = policyUris => {
    const { selectedPolicies } = this.state;

    let newSelectedPolicies;
    if (selectedPolicies.includes(policyUris[0])) {
      newSelectedPolicies = selectedPolicies.filter(
        item => !policyUris.includes(item)
      );
    } else {
      newSelectedPolicies = selectedPolicies.concat(policyUris);
    }

    this.setState({ selectedPolicies: newSelectedPolicies });
  };

  toggleAll = () => {
    const { selectedPolicies } = this.state;
    const policies = this.getPoliciesByOrder();
    this.setState({
      selectedPolicies:
        selectedPolicies.length === policies.length ? [] : policies
    });
  };

  deletePolicy = policyUri => {
    const {
      clearNotifications,
      createNotification,
      removePolicy,
      policies
    } = this.props;
    const policy = policies.find(item => item.$uri === policyUri);
    const notificationFunc = id => this.undoDeletePolicy(policy, id);

    clearNotifications();
    createNotification({
      message: "The policy has been removed.",
      action: {
        text: "Undo",
        func: notificationFunc
      },
      timer: UNDO_REMOVE_POLICY_TIMER
    });

    removePolicy(policy);
  };

  undoDeletePolicy = (policy, notificationId) => {
    const {
      cancelRemovePolicy,
      createNotification,
      removeNotification
    } = this.props;
    cancelRemovePolicy(policy);
    removeNotification(notificationId);
    createNotification({
      message: "Policy removal undone.",
      timer: UNDO_REMOVE_POLICY_TIMER
    });
  };

  deleteSelectedPolicies = () => {
    const { selectedPolicies } = this.state;
    const { commit } = this.props;
    const policiesToDelete = selectedPolicies.map(policy => ({
      $uri: policy.$uri,
      requiresDelete: true
    }));

    commit(policiesToDelete);
    this.setState({ selectedPolicies: [] });
  };

  editPolicy = policy => {
    const {
      setCurrentService,
      setCurrentResource,
      setEntityFilterTerm
    } = this.props;

    setEntityFilterTerm(policy.entity.label.toLowerCase());
    setCurrentResource(policy.resource.$uri);
    setCurrentService(policy.service.$uri);
  };

  openDelete = () => {
    const { selectedPolicies, columns, orderBy } = this.state;
    const { closeModal, openDeleteModal, saving } = this.props;

    openDeleteModal({
      policies: selectedPolicies,
      deletePolicies: this.deleteSelectedPolicies,
      close: closeModal,
      columns,
      orderBy,
      saving
    });
  };

  render() {
    const { columns, orderBy, selectedPolicies } = this.state;
    const { canEditPolicies, loading, saving } = this.props;
    const policies = this.getPoliciesByOrder();

    return (
      <div>
        <DocumentTitle title={this.title} />
        <div className="l-listview">
          <ListViewActionBar
            canDelete={canEditPolicies}
            disableDelete={selectedPolicies.length <= 0 || saving || loading}
            openDeleteModal={this.openDelete}
            saving={saving}
            selectedCount={selectedPolicies.length}
            selectedOrder={orderBy[0]}
            selectOrder={this.updateOrder}
          />
          <ListViewTable
            checkAll={this.toggleAll}
            columns={columns}
            deleteRow={this.deletePolicy}
            disabled={saving || loading || !policies.length}
            editRow={this.editPolicy}
            openRoles={this.openRoles}
            openResources={this.openResources}
            orderBy={orderBy}
            policies={policies}
            selectedRows={selectedPolicies}
            selectRows={this.selectPolicies}
            showActions={canEditPolicies}
            parentUrl="/listview"
          />
        </div>
      </div>
    );
  }
}

ListView.propTypes = {
  push: PropTypes.func.isRequired,
  cancelRemovePolicy: PropTypes.func.isRequired,
  canEditPolicies: PropTypes.bool.isRequired,
  clearNotifications: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  createNotification: PropTypes.func.isRequired,
  commit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  removeNotification: PropTypes.func.isRequired,
  removePolicy: PropTypes.func.isRequired,
  openDeleteModal: PropTypes.func.isRequired,
  policies: PropTypes.arrayOf(PolicyShape).isRequired,
  policiesByGroup: PropTypes.arrayOf(ListTablePolicyShape).isRequired,
  policiesByRole: PropTypes.arrayOf(ListTablePolicyShape).isRequired,
  policiesByService: PropTypes.arrayOf(ListTablePolicyShape).isRequired,
  saving: PropTypes.bool.isRequired,
  setCurrentResource: PropTypes.func.isRequired,
  setCurrentResourceType: PropTypes.func.isRequired,
  setCurrentRole: PropTypes.func.isRequired,
  setCurrentService: PropTypes.func.isRequired,
  setEntityFilterTerm: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  canEditPolicies: userSelectors.canEditPolicies(state),
  loading: state.policies.loading,
  policies: policiesSelectors.get(state),
  policiesByGroup: listTablePoliciesSelector.getByGroup(state),
  policiesByRole: listTablePoliciesSelector.getByRole(state),
  policiesByService: listTablePoliciesSelector.getByService(state),
  saving: state.policies.saving
});

const mapDispatchToProps = {
  closeModal: policies.closeModal,
  cancelRemovePolicy: policies.removeCancel,
  clearNotifications: notifications.clear,
  createNotification: notifications.create,
  removeNotification: notifications.remove,
  commit: policies.commit,
  removePolicy: policies.remove,
  openDeleteModal: policies.openDeleteModal,
  setCurrentGroup: groups.setCurrent,
  setCurrentResource: resources.setCurrent,
  setCurrentResourceType: resourceTypes.setCurrent,
  setCurrentRole: roles.setCurrent,
  setCurrentService: services.setCurrent,
  setCurrentUser: users.setCurrent,
  setEntityFilterTerm: users.setFilterTerm,
  push
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ListView);
